import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import { type PlanPreview } from '@/designs-dev/plan/types';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import IntakeFlow from '@/karehero-app/tell-us-once/intake/Flow';
import ReviewScreen from '@/karehero-app/tell-us-once/intake/ReviewScreen';
import CalculatorBar from '@/karehero-app/tell-us-once/benefits/CalculatorBar';
import CarriedScreen from '@/karehero-app/tell-us-once/benefits/CarriedScreen';
import ResultScreen from '@/karehero-app/tell-us-once/benefits/ResultScreen';
import {
  AA_BAR,
  AA_CARRIED,
  AA_CHAT,
  AA_START,
} from '@/karehero-app/tell-us-once/AttendanceAllowance.content';

const noop = () => {};

/**
 * The screens that stand for each stage, keyed by the stage's title.
 *
 * A .tsx beside the plan's content module for the same reason `loft/elements`
 * sits beside the manifest: the plan is data and a screen is a component, and
 * keeping the two apart is what lets the content module stay a plain .ts.
 *
 * A few each, not one. Every stage here is a conversation followed by something
 * that comes out of it, and a single frame of the something reads as a form: the
 * asking has to be in the strip or the row's whole argument is missing. Still
 * not a walk, though — the full prototype is a click away in the menu.
 *
 * Infrastructure has none on purpose: it is internal, and there is nothing to
 * show.
 */
export const tellUsOncePreviews: Record<string, PlanPreview[]> = {
  Onboarding: [
    {
      label: 'The conversation',
      render: () => <IntakeFlow initial='chat-v2' option={2} />,
    },
    {
      label: 'What we read from it',
      render: () => <IntakeFlow initial='review-facts' option={2} />,
    },
  ],
  'Benefits calculator': [
    {
      label: 'Opens on what we hold',
      render: () => <CarriedScreen onStart={noop} bar={<CalculatorBar />} />,
    },
    {
      label: 'Ends on a claim',
      render: () => (
        <ResultScreen onStart={noop} onTalk={noop} bar={<CalculatorBar />} />
      ),
    },
  ],
  'Attendance Allowance': [
    {
      label: 'What the claim already has',
      render: () => (
        <CarriedScreen
          onStart={noop}
          bar={<CalculatorBar content={AA_BAR} />}
          content={AA_START}
          carried={AA_CARRIED}
        />
      ),
    },
    {
      label: 'The care questions',
      render: () => (
        <ChatV2
          questions={AA_CHAT}
          startAt={1}
          fresh
          onExit={noop}
          bar={<CalculatorBar content={AA_BAR} />}
        />
      ),
    },
    {
      label: 'The review',
      render: () => <ReviewScreen />,
    },
  ],
  'CAM insight': [
    {
      label: 'The Overview tab, with an intake behind it',
      // The CAM's surface is not a phone.
      screen: 'desktop',
      render: () => (
        <ControlPanel embedded initialTab='overview' intake='consented' />
      ),
    },
  ],
};
